<!-- src/views/admin/AdminManageVehiclesView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">

        <!-- 1. HEADER & COMMAND FILTERS -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 animate-fadeIn relative overflow-hidden text-left">
          <div class="absolute top-0 right-0 p-12 opacity-5"><TruckIcon class="h-32 w-32 text-white" /></div>
          
          <div class="relative z-10">
            <div class="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-8 text-left">
              
              <div class="flex items-center gap-5 text-left">
                <div class="bg-[#20FCF1]/10 p-4 rounded-[24px] border border-[#20FCF1]/20">
                   <TruckIcon class="h-10 w-10 text-[#20FCF1]" />
                </div>
                <div class="text-left">
                  <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none text-left">Fleet Command Centre</h1>
                  <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4 text-left">Asset Intelligence • Compliance & Deployment Registry</p>
                </div>
              </div>
              
              <div class="flex flex-wrap items-center gap-3 text-left">
                <router-link v-slot="{ href, navigate }" v-if="hasFleetClearance" :to="{ name: 'AdminVehicleEditor', params: { id: 'new' }}" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2 no-underline">
                    <PlusIcon class="h-4 w-4" /> <span>Initialise Asset</span>
                  </a>
                </router-link>

                <!-- 🛡️ NAVIGATION STANDARDISED: Updated button text to 'Return' and matched padding -->
                <router-link v-slot="{ href, navigate }" to="/client-admin" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 no-underline">
                    <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
                  </a>
                </router-link>
              </div>
            </div>

            <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-6 bg-white/5 p-8 rounded-[32px] border border-white/10">
              <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 items-end w-full text-left">
                <div class="flex flex-col gap-1.5 text-left">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Search Fleet</span>
                  <input v-model="filters.search" type="text" placeholder="Registration or Driver..." class="filter-input-dark" />
                </div>

                <div class="flex flex-col gap-1.5 text-left">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Registry Mode</span>
                  <select v-model="activeMode" class="filter-select-dark">
                    <option value="fleet">Asset Compliance Registry</option>
                    <option value="audits">Pre-Use Check Archive</option>
                  </select>
                </div>

                <div class="flex flex-col gap-1.5 text-left">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Weekly Compliance</span>
                  <select v-model="filters.compliance" class="filter-select-dark">
                    <option value="all">All Audit Statuses</option>
                    <option value="checked">Checked This Week</option>
                    <option value="missing">Missing Checks</option>
                  </select>
                </div>

                <div class="flex flex-col gap-1.5 text-left">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Operational Status</span>
                  <select v-model="filters.status" class="filter-select-dark">
                    <option value="all">All Statuses</option>
                    <option value="Active">Active</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="SORN">SORN</option>
                  </select>
                </div>
              </div>

              <!-- 🛡️ RE-STYLED EXPORT BUTTON: Styled exactly like the dark-glass Engineering/Training export buttons -->
              <button 
                @click="exportFleetRegistry"
                :disabled="exporting || paginatedVehicles.length === 0"
                class="px-6 bg-white/10 border border-white/20 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 h-[46px] disabled:opacity-50 shadow-lg cursor-pointer border-none shrink-0"
              >
                <template v-if="exporting">
                  <div class="animate-spin h-3 w-3 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
                  <span>Syncing...</span>
                </template>
                <template v-else>
                  <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Export Current List</span>
                </template>
              </button>
            </div>
          </div>
        </div>

        <!-- 🛡️ 2. SENTINEL INTELLIGENCE & INTEGRATED METRICS CARD -->
        <div class="mb-12 animate-fadeIn text-left">
          <div class="bg-white border-2 border-slate-100 rounded-[40px] shadow-2xl overflow-hidden relative">
            <div class="bg-[#0A1630] px-10 py-6 border-b-4 border-[#20FCF1] flex flex-col md:flex-row justify-between items-center gap-6">
               <div class="flex items-center gap-4">
                  <div class="bg-[#20FCF1]/10 p-3 rounded-2xl">
                    <SparklesIcon class="h-6 w-6 text-[#20FCF1]" />
                  </div>
                  <div>
                    <h2 class="text-xl font-black text-white uppercase tracking-tight leading-none">Sentinel Intelligence</h2>
                    <p class="text-[8px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mt-2">Active Fleet Synthesis • Deterministic Audit Logic</p>
                  </div>
               </div>
               
               <button 
                 @click="triggerSynthesis" 
                 :disabled="isAiLoading"
                 class="px-6 py-3 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg"
               >
                 <span v-if="isAiLoading" class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                 <SignalIcon v-else class="h-4 w-4" />
                 <span>{{ isAiLoading ? 'Synthesising...' : 'Synchronise Intelligence' }}</span>
               </button>
            </div>

            <div class="p-8 md:p-12 text-left">
               <!-- 📊 INTEGRATED METRICS ROW inside Sentinel Intelligence Card -->
               <div class="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 border-b border-slate-100 pb-8 text-left animate-fadeIn">
                 <div v-for="stat in fleetStats" :key="stat.label" class="flex items-center gap-4 text-left">
                   <div class="relative w-12 h-12 flex items-center justify-center text-center">
                     <svg class="absolute inset-0 w-full h-full" viewBox="0 0 24 24" fill="none">
                         <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" :fill="stat.color" fill-opacity="0.1" :stroke="stat.color" stroke-width="1.5"/>
                     </svg>
                     <component :is="stat.icon" class="w-5 h-5" :style="{ color: stat.color }" />
                   </div>
                   <div class="text-left">
                     <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 text-left">{{ stat.label }}</p>
                     <h4 class="text-xl font-black text-[#0A1630] leading-none text-left">{{ stat.value }}</h4>
                   </div>
                 </div>
               </div>

               <!-- Initial State -->
               <div v-if="!aiReport && !isAiLoading" class="flex flex-col items-center justify-center py-12 text-center">
                  <div class="bg-slate-50 p-6 rounded-full mb-6">
                    <CpuChipIcon class="h-10 w-10 text-slate-200" />
                  </div>
                  <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">Awaiting Initialisation</h3>
                  <p class="text-xs text-slate-400 font-bold mt-2 max-w-sm">Trigger a synchronisation to audit current asset statuses, check compliance, and identify expiring certifications.</p>
               </div>

               <!-- 🛡️ LOCAL SYNTHESIS CONTENT -->
               <div v-else-if="aiReport" class="grid grid-cols-1 lg:grid-cols-3 gap-10">
                  <div class="lg:col-span-2 space-y-8">
                     <div class="border-l-4 border-[#20FCF1] pl-6">
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-3">Executive Summary</h4>
                        <p class="text-sm font-medium text-slate-600 leading-relaxed italic">
                          "{{ aiReport.summary }}"
                        </p>
                     </div>

                     <div>
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Priority Interventions</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div v-for="(action, idx) in aiReport.actions" :key="idx" class="bg-slate-50 p-5 rounded-3xl border border-slate-100 flex flex-col justify-between">
                              <div>
                                <span class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest block mb-2">{{ action.type }}</span>
                                <p class="text-xs font-black text-[#0A1630] leading-tight mb-3 uppercase tracking-tight">{{ action.instruction }}</p>
                              </div>
                              <p class="text-[10px] text-slate-500 font-medium leading-relaxed">{{ action.reason }}</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="bg-[#0A1630]/5 p-8 rounded-[32px] border border-slate-100 h-fit">
                    <h4 class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.3em] mb-6 border-b border-[#0A1630]/10 pb-4">Compliance Risks</h4>
                    <div class="space-y-6">
                       <div v-for="(trend, idx) in aiReport.trends" :key="idx" class="flex items-start gap-4">
                          <div :class="trend.isCritical ? 'bg-rose-500' : 'bg-[#20FCF1]'" class="h-2 w-2 rounded-full shrink-0 mt-1.5 shadow-sm"></div>
                          <div>
                            <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-tight">{{ trend.label }}</p>
                            <p class="text-[11px] text-slate-500 font-medium mt-1 leading-tight">{{ trend.value }}</p>
                          </div>
                       </div>
                    </div>
                    <div class="mt-8 pt-6 border-t border-[#0A1630]/10 text-[9px] font-black text-slate-400 uppercase">
                      Analysis Depth: Active Fleet Profiles
                    </div>
                  </div>
               </div>

               <!-- Loading State -->
               <div v-if="isAiLoading" class="flex flex-col items-center justify-center py-12">
                  <div class="animate-spin h-12 w-12 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
                  <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">Aggregating Cross-Asset Data & Synthesising Findings...</p>
               </div>
            </div>
          </div>
        </div>

        <!-- 4. DATA DISPLAY -->
        <div v-if="loading" class="text-center py-24">
          <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-4"></div>
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Initialising Fleet Registry...</p>
        </div>

        <div v-else-if="paginatedVehicles.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 animate-fadeIn shadow-inner">
           <MagnifyingGlassIcon class="h-14 w-14 text-slate-200 mx-auto mb-6 opacity-30" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Registry Neutral</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">No matching assets identified in this audit cycle.</p>
        </div>

        <div v-else class="space-y-4 animate-fadeIn text-left">
          <div 
            v-for="vehicle in paginatedVehicles" 
            :key="vehicle.id"
            class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group text-left"
          >
            <div class="flex flex-col xl:flex-row items-center justify-between gap-6 text-left">
              
              <div class="flex items-center gap-6 w-full xl:w-[25%] shrink-0 text-left">
                <div class="relative h-20 w-20 shrink-0 bg-white p-1 rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex items-center justify-center">
                  <img v-if="qrCodeUrls[vehicle.id]" :src="qrCodeUrls[vehicle.id]" alt="QR" class="w-full h-full object-contain" />
                  <div v-else class="animate-pulse bg-slate-50 w-full h-full rounded-lg"></div>
                </div>
                <div class="min-w-0 text-left">
                  <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none mb-2 text-left">
                    {{ vehicle.registration }}
                  </h3>
                  <div class="flex items-center gap-2 text-left">
                    <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest bg-[#EBFDF8] px-2 py-0.5 rounded-md border border-[#20FCF1]/10">
                        {{ vehicle.assignedDriverName || 'Unassigned Driver' }}
                    </span>
                    <span v-if="vehicle.status !== 'Active'" class="text-[8px] font-black uppercase px-2 py-0.5 rounded-md bg-amber-50 text-amber-600 border border-amber-100">{{ vehicle.status }}</span>
                  </div>
                </div>
              </div>

              <!-- 🛡️ INTERACTIVE COMPLIANCE PULSE: TRI-STATE -->
              <div class="flex flex-col gap-3 w-full xl:w-[30%] shrink-0 px-8 border-x border-slate-50 text-left">
                <div class="flex items-center justify-between text-left">
                   <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest">7-Day Compliance Pulse</p>
                   <span :class="vehicle.hasBeenCheckedThisWeek ? 'text-emerald-500' : 'text-rose-500'" class="text-[8px] font-black uppercase tracking-widest flex items-center gap-1">
                      <div class="w-1 h-1 rounded-full bg-current animate-pulse"></div>
                      {{ vehicle.hasBeenCheckedThisWeek ? 'Synchronised' : 'Missing Check' }}
                   </span>
                </div>
                
                <div class="flex items-center justify-between gap-1 text-left">
                   <div v-for="(pulseEntry, index) in vehicle.pulse" :key="index" class="flex flex-col items-center gap-1.5 flex-1 text-left">
                      <!-- 🛡️ CLICK HANDSHAKE: Targeted Path-Based Routing -->
                      <div 
                        @click.stop="pulseEntry.id ? viewAudit(pulseEntry.id) : null"
                        :class="[
                            pulseEntry.status === 'pass' ? 'bg-[#20FCF1] shadow-[0_0_12px_rgba(56,227,176,0.4)] hover:scale-125 cursor-pointer z-20' : '',
                            pulseEntry.status === 'fail' ? 'bg-[#EF4444] shadow-[0_0_12px_rgba(239,68,68,0.4)] animate-pulse hover:scale-125 cursor-pointer z-20' : '',
                            pulseEntry.status === 'missing' ? 'bg-[#0A1630]/5 border border-slate-100 pointer-events-none' : ''
                        ]" 
                        class="w-full h-2.5 rounded-full transition-all duration-300 relative group"
                        v-tooltip="pulseEntry.status !== 'missing' ? `View Audit: ${pulseEntry.status.toUpperCase()}` : pulseDays[index]"
                      >
                         <div v-if="pulseEntry.id" class="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 rounded-full transition-opacity"></div>
                      </div>
                      <span class="text-[7px] font-black text-slate-300 uppercase tracking-tighter">{{ pulseDays[index] }}</span>
                   </div>
                </div>
              </div>

              <div class="flex-grow px-8 min-w-0 grid grid-cols-3 gap-6 text-left">
                 <div class="text-left">
                    <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 text-left">Last Entry</p>
                    <p v-if="vehicle.lastCheckDate" class="text-[11px] font-black text-[#0A1630] uppercase tracking-widest text-left">{{ formatDate(vehicle.lastCheckDate) }}</p>
                    <p v-else class="text-[9px] font-black text-rose-500 uppercase tracking-widest bg-rose-50 px-2 py-0.5 rounded-md inline-block">No Record</p>
                 </div>
                 <div class="text-left border-x border-slate-50 px-4">
                    <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 text-left">MOT Expiry</p>
                    <p :class="getDateClass(vehicle.motExpiry)" class="text-[11px] font-black uppercase text-left">{{ formatDate(vehicle.motExpiry) }}</p>
                 </div>
                 <div class="text-left">
                    <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 text-left">Service Due</p>
                    <p :class="getDateClass(vehicle.serviceDue)" class="text-[11px] font-black uppercase text-left">{{ formatDate(vehicle.serviceDue) }}</p>
                 </div>
              </div>

              <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-6 text-left">
                 <router-link 
                  v-if="hasFleetClearance"
                  :to="{ name: 'AdminVehicleEditor', params: { id: vehicle.id }}" 
                  class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2"
                >
                  <WrenchIcon class="h-4 w-4" /> <span>Manage</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Symmetrical Pagination Controls (Forced h-11 heights) -->
        <div v-if="totalPages > 1 || itemsPerPage !== 25" class="flex flex-col sm:flex-row justify-center items-center gap-6 mt-12 no-print">
          <div class="flex items-center gap-4">
            <button 
              @click="prevPage" 
              :disabled="currentPage === 1"
              class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
            >
              &larr; Prev
            </button>
            <span class="text-xs font-black text-[#0A1630] uppercase tracking-widest">
              Page {{ currentPage }} of {{ totalPages || 1 }}
            </span>
            <button 
              @click="nextPage" 
              :disabled="currentPage === totalPages || totalPages === 0"
              class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
            >
              Next &rarr;
            </button>
          </div>
          
          <!-- Symmetrical Items Per Page Selector (Forced h-11 height) -->
          <div class="flex items-center gap-3">
            <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest">Display:</span>
            <select 
              v-model="itemsPerPage" 
              class="h-11 bg-[#0A1630] text-white rounded-xl px-5 text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer shadow-md appearance-none pr-8 text-center border-none"
              style="background-image: url('data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2320FCF1\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E'); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 0.75rem;"
            >
              <option :value="25">25 Rows</option>
              <option :value="50">50 Rows</option>
              <option :value="100">100 Rows</option>
            </select>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import { db } from '@/firebaseConfig';
import { collection, query, where, getDocs, Timestamp, doc, getDoc, setDoc } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import * as XLSX from 'xlsx';
import QRCode from 'qrcode';
import { 
  TruckIcon, ArrowUturnLeftIcon, WrenchIcon, TableCellsIcon, 
  MagnifyingGlassIcon, CheckBadgeIcon, ShieldExclamationIcon, 
  PlusIcon, SparklesIcon, CpuChipIcon, SignalIcon
} from '@heroicons/vue/24/outline';

const router = useRouter();
const userStore = useUserStore();

const loading = ref(true);
const exporting = ref(false);
const vehicles = ref([]);
const qrCodeUrls = reactive({});

// 🛡️ SYNTHESIS STATE REGISTRY
const aiReport = ref(null);
const isAiLoading = ref(false);

// PAGINATION STATE (Defaulting to 25 rows with h-11 sizing logic)
const currentPage = ref(1);
const itemsPerPage = ref(25);

// 🛡️ STICKY SELECTIONS & FILTERS: Write selections to localStorage dynamically
const activeMode = ref(localStorage.getItem('fleet_sticky_mode') || 'fleet');

const filters = reactive({
  search: localStorage.getItem('fleet_sticky_search') || '',
  status: localStorage.getItem('fleet_sticky_status') || 'all',
  compliance: localStorage.getItem('fleet_sticky_compliance') || 'all'
});

watch(activeMode, (val) => localStorage.setItem('fleet_sticky_mode', val));
watch(() => filters.search, (val) => localStorage.setItem('fleet_sticky_search', val));
watch(() => filters.status, (val) => localStorage.setItem('fleet_sticky_status', val));
watch(() => filters.compliance, (val) => localStorage.setItem('fleet_sticky_compliance', val));

// Reset page back to 1 on mode/filter updates
watch([activeMode, () => filters.search, () => filters.status, () => filters.compliance, itemsPerPage], () => {
  currentPage.value = 1;
});

const pulseDays = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const startOfCurrentRailWeek = computed(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = (day + 1) % 7;
    const saturday = new Date(now);
    saturday.setDate(now.getDate() - diff);
    saturday.setHours(0, 0, 0, 0);
    return saturday;
});

const fleetStats = computed(() => {
  const missingChecks = filteredVehicles.value.filter(v => !v.hasBeenCheckedThisWeek).length;
  return[
    { label: 'Fleet Assets', value: vehicles.value.length, color: '#6366F1', icon: TruckIcon },
    { label: 'Active Fleet', value: vehicles.value.filter(v => v.status === 'Active').length, color: '#20FCF1', icon: CheckBadgeIcon },
    { label: 'Missing Checks', value: missingChecks, color: missingChecks > 0 ? '#EF4444' : '#20FCF1', icon: ShieldExclamationIcon },
    { label: 'In Maintenance', value: vehicles.value.filter(v => v.status === 'Maintenance').length, color: '#FBBF24', icon: WrenchIcon },
  ];
});

const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric'});
};

const getDateClass = (dateStr) => {
    if (!dateStr) return 'text-slate-300';
    const date = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'text-rose-600 font-black italic';
    if (diffDays < 30) return 'text-amber-500 font-black';
    return 'text-[#20FCF1] font-black';
};

/** 🛡️ PATH-BASED REDIRECT: Hardened navigation for TECS Environment */
const viewAudit = (id) => {
  if (!id) return;
  router.push({ 
    path: '/admin/fleet-audit', 
    query: { submissionId: id } 
  });
};

const filteredVehicles = computed(() => {
  return vehicles.value.filter(v => {
    const matchSearch = !filters.search || 
      (v.registration || '').toLowerCase().includes(filters.search.toLowerCase()) ||
      (v.assignedDriverName || '').toLowerCase().includes(filters.search.toLowerCase());
    
    const matchStatus = filters.status === 'all' || v.status === filters.status;

    let matchCompliance = true;
    if (filters.compliance === 'checked') matchCompliance = v.hasBeenCheckedThisWeek;
    if (filters.compliance === 'missing') matchCompliance = !v.hasBeenCheckedThisWeek;

    return matchSearch && matchStatus && matchCompliance;
  });
});

// PAGINATION MATH
const totalPages = computed(() => {
  return Math.ceil(filteredVehicles.value.length / itemsPerPage.value);
});

const paginatedVehicles = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredVehicles.value.slice(start, end);
});

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

// 🛡️ UNCONDITIONAL PLATFORM AUTHORITY OVERRIDE: Simplifies permissions so all Command Access Admins can manage everything
const hasFleetClearance = computed(() => {
  return userStore.role === 'super-admin' || 
         userStore.isAdmin || 
         userStore.hasAdminAccess ||
         userStore.permissions?.canViewFleet === true ||
         userStore.permissions?.canManageFleet === true;
});

// 🛡️ DETERMINISTIC SYNTHESIS ENGINE
const triggerSynthesis = async () => {
    isAiLoading.value = true;
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) { alert("Identity context missing."); isAiLoading.value = false; return; }
    
    await new Promise(r => setTimeout(r, 1500));

    try {
        const total = vehicles.value.length;
        const missingCount = vehicles.value.filter(v => !v.hasBeenCheckedThisWeek).length;
        const activeCount = vehicles.value.filter(v => v.status === 'Active').length;
        const maintenanceCount = vehicles.value.filter(v => v.status === 'Maintenance').length;

        // Evaluate critical upcoming expirations
        const today = new Date();
        let motCritical = 0;
        let serviceCritical = 0;
        vehicles.value.forEach(v => {
            if (v.motExpiry) {
                const days = Math.ceil((new Date(v.motExpiry) - today) / (1000 * 60 * 60 * 24));
                if (days >= 0 && days < 30) motCritical++;
            }
            if (v.serviceDue) {
                const days = Math.ceil((new Date(v.serviceDue) - today) / (1000 * 60 * 60 * 24));
                if (days >= 0 && days < 30) serviceCritical++;
            }
        });

        const complianceRate = total > 0 ? Math.round(((total - missingCount) / total) * 100) : 100;

        const report = {
            summary: `Fleet audit of ${total} configured assets (${activeCount} currently active) indicates an operational compliance profile of ${complianceRate}%. Identified ${missingCount} vehicles with missing pre-use inspections during this Rail Week cycle. Maintenance diagnostics report ${maintenanceCount} vehicles currently offline.`,
            trends: [
                { label: 'Weekly Check Slip', value: `${missingCount} Assets outstanding`, isCritical: missingCount > 0 },
                { label: 'MOT Compliance', value: `${motCritical} Expirations inside 30 days`, isCritical: motCritical > 0 },
                { label: 'Service Cycles', value: `${serviceCritical} Services scheduled soon`, isCritical: serviceCritical > 0 }
            ],
            actions: [
                { type: 'Operation Alert', instruction: `Enforce inspections on ${missingCount} assets`, reason: 'Outstanding weekly pre-use checklists violate safety alignment mandates.' },
                { type: 'Garage Priority', instruction: `Address ${maintenanceCount} offline vehicles`, reason: 'Offline assets constrain deployment resources and payload capacities.' }
            ],
            createdAt: new Date().toISOString()
        };

        const reportRef = doc(db, 'clients', cid, 'fleet_intelligence', 'latest');
        await setDoc(reportRef, { ...report, clientId: cid }, { merge: true });
        aiReport.value = report;

    } catch (e) {
        console.error("Synthesis Protocol Failure:", e);
        alert("Registry Handshake Failure: Insufficient authorisation to save intelligence log.");
    } finally {
        isAiLoading.value = false;
    }
};

const fetchLatestAiReport = async (cid) => {
  try {
    const docSnap = await getDoc(doc(db, 'clients', cid, 'fleet_intelligence', 'latest'));
    if (docSnap.exists()) {
      aiReport.value = docSnap.data();
    }
  } catch (err) {
    console.warn("Fleet Intelligence Registry Neutral.");
  }
};

const fetchData = async () => {
  loading.value = true;
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) return;

    await fetchLatestAiReport(cid);

    const qFleet = query(collection(db, 'clients', cid, 'vehicles'));
    const fleetSnap = await getDocs(qFleet);
    const rawFleet = fleetSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const historyLimit = new Date();
    historyLimit.setDate(historyLimit.getDate() - 14);
    const qChecks = query(
        collection(db, 'vehicleChecks'), 
        where('clientId', '==', cid), 
        where('submittedAt', '>=', Timestamp.fromDate(historyLimit))
    );
    const checksSnap = await getDocs(qChecks);
    const allRecentChecks = checksSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    for (const v of rawFleet) {
       const url = `${window.location.origin}/vehicle-check?reg=${v.registration}`;
       try { qrCodeUrls[v.id] = await QRCode.toDataURL(url, { width: 200, margin: 1 }); } catch (err) { console.error(err); }
    }
    
    const currentWeekStartMs = startOfCurrentRailWeek.value.getTime();
    
    vehicles.value = rawFleet.map(v => {
        const vChecks = allRecentChecks.filter(c => c.vehicleId === v.id || (c.vehicleReg && c.vehicleReg.toUpperCase() === v.registration?.toUpperCase()));
        
        vChecks.sort((a, b) => (b.submittedAt?.toMillis() || 0) - (a.submittedAt?.toMillis() || 0));

        const latestCheck = vChecks[0];
        let lastCheckDate = latestCheck?.submittedAt ? latestCheck.submittedAt.toDate().toISOString() : null;
        let hasBeenCheckedThisWeek = false;
        
        const pulse = [
          { status: 'missing', id: null }, { status: 'missing', id: null }, { status: 'missing', id: null }, 
          { status: 'missing', id: null }, { status: 'missing', id: null }, { status: 'missing', id: null }, 
          { status: 'missing', id: null }
        ];

        vChecks.forEach(check => {
            if (check.submittedAt) {
                const checkDate = check.submittedAt.toDate();
                const checkMs = checkDate.getTime();

                if (checkMs >= currentWeekStartMs) {
                    hasBeenCheckedThisWeek = true;
                    let dayOfWeek = checkDate.getDay(); 
                    let railIndex = (dayOfWeek + 1) % 7; 
                    
                    const statusVal = (check.status || '').toLowerCase();
                    const hasFault = statusVal === 'fail' || check.faultsFound === true || (check.defects && check.defects.length > 0);
                    const currentStatus = hasFault ? 'fail' : 'pass';

                    if (pulse[railIndex].status !== 'fail') {
                        pulse[railIndex] = { status: currentStatus, id: check.id };
                    }
                }
            }
        });

        return { ...v, lastCheckDate, hasBeenCheckedThisWeek, pulse };
    });

  } catch (err) { console.error("Sync Error:", err); } 
  finally { loading.value = false; }
};

const exportFleetRegistry = () => {
  exporting.value = true;
  try {
    const data = filteredVehicles.value.map(v => ({
      'Registration': v.registration?.toUpperCase(),
      'Primary Driver': v.assignedDriverName || 'UNASSIGNED',
      'Status': v.status?.toUpperCase(),
      'Last Inspected': v.lastCheckDate ? formatDate(v.lastCheckDate) : 'NO RECORD',
      'Weekly Compliance': v.hasBeenCheckedThisWeek ? 'AUTHORISED' : 'MISSING',
      'MOT Expiry': v.motExpiry ? formatDate(v.motExpiry) : 'N/A',
      'Service Due': v.serviceDue ? formatDate(v.serviceDue) : 'N/A'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fleet_Asset_Registry");
    XLSX.writeFile(wb, `Fleet_Status_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
  } catch (err) { alert("Registry Export Failed"); } 
  finally { exporting.value = false; }
};

watch(activeMode, (newVal) => { if (newVal === 'audits') router.push({ path: '/admin/fleet-audit' }); });

onMounted(() => { 
  document.title = "Fleet Command Centre | Sentinel Safe";
});

watchEffect(() => {
  const cid = userStore.clientId || userStore.userProfile?.clientId;
  if (cid && userStore.isAuthReady) fetchData();
});
</script>

<style scoped>
.filter-input-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-sm font-bold text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all placeholder-slate-500 text-left; }
.filter-select-dark {
  @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none text-left;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2338E3B0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1rem;
}
.animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
</style>